/* eslint complexity: ["error", 10] -- longer functions use case */
import { useEffect, useRef } from "react";

import isNil from "lodash/isNil";

import { TasksStatusId } from "@/constants/Tasks";
import { TasksTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useToast } from "@/hooks/useToast";
import { useUnmount } from "@/hooks/useUnmount";
import useAppMonitor from "@/services/AppMonitor";
import { useUpdateTaskMutation } from "@/store/api/tasks";
import { useAppSelector } from "@/store/hooks";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import {
  selectTaskDetailsData,
  useTaskDetailsModalDispatch,
} from "@/store/reducers/modals/taskDetailsModal";
import TaskBasicDetails from "@/ui/components/tasks/TaskBasicDetails";
import TaskDetailsModalWrapper, {
  FooterActionLabel,
} from "@/ui/components/tasks/TaskDetailsModalWrapper";
import { FullScreenSpinner } from "@/ui/core";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

const baseTestID = TasksTestID.TaskDetails;

const Modal = () => {
  const { captureException } = useAppMonitor();
  const [updateTask, { isLoading }] = useUpdateTaskMutation();
  const { externalId: appUserId, isPatient } = useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector();

  const { showToast } = useToast();
  const renderCountRef = useRef<number>(1);
  useEffect(() => {
    renderCountRef.current = renderCountRef.current + 1;
  });

  const { resetTaskDetailsData } = useTaskDetailsModalDispatch();

  useUnmount({ onUnmount: resetTaskDetailsData });

  const taskDetailsData = useAppSelector(selectTaskDetailsData);
  const { description, duration, patientName, selectedUserId, taskId, title } =
    taskDetailsData || {};
  if (
    isNil(title) ||
    isNil(description) ||
    isNil(duration) ||
    !isNotNilNotEmpty(selectedUserId) ||
    !isNotNilNotEmpty(taskId)
  ) {
    // capture renderCount in error message to enable better debugging and
    // to distinguish this happening on initial render, a true critical error,
    // versus on subsequent render, which means initial render succeeded and something just goes wrong later
    captureException(
      new Error(
        `lab-request modal is missing required params on render #${renderCountRef.current}`,
      ),
    );
    if (isWeb()) {
      router.dismiss();
    }
    return <FullScreenSpinner />;
  }

  const handleCompleteTaskPress = async () => {
    try {
      await updateTask({
        appUserId,
        isPatient,
        linkedPatients: proxyPatients || [],
        selectedUserId,
        taskId,
        taskStatusId: TasksStatusId.Done,
      }).unwrap();

      showToast({
        text: "Lab order completed",
        variant: "success",
      });
    } catch (error) {
      captureException(new Error("Error updating task", { cause: error }));
      showToast({
        text: "Failed to complete lab order",
        variant: "error",
      });
    } finally {
      router.dismiss();
    }
  };

  return (
    <TaskDetailsModalWrapper
      footerRightButtonLabel={FooterActionLabel.Complete}
      isLoading={isLoading}
      onFooterRightButtonPress={handleCompleteTaskPress}
    >
      <TaskBasicDetails
        description={description}
        duration={duration}
        patientName={patientName}
        testID={baseTestID}
        title={title}
      />
    </TaskDetailsModalWrapper>
  );
};

export default Modal;
